// Confetti on submit, no library: a few dozen absolutely-positioned pieces
// with random drift, a message card, and gone after ~2.6 s. Reduced motion
// gets the message without the confetti (design.css).
import { useEffect, useMemo } from "react";

export interface CelebrationProps {
  show: boolean;
  message?: string;
  detail?: string;
  /** Inside a positioned box (the board) rather than the whole window. */
  contained?: boolean;
  night?: boolean;
  pieces?: number;
  durationMs?: number;
  onDone?: () => void;
}

export function Celebration({ show, message, detail, contained, night, pieces = 60, durationMs = 2600, onDone }: CelebrationProps) {
  // New pieces every time it is shown, so two submits in a row don't look identical.
  const bits = useMemo(() => Array.from({ length: pieces }, (_, i) => ({
    key: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    dur: 1.8 + Math.random() * 1.2,
    dx: (Math.random() - 0.5) * 240,
    spin: (Math.random() - 0.5) * 1440,
    tone: 1 + (i % 5),
    w: 6 + Math.random() * 8
  })), [show, pieces]);

  useEffect(() => {
    if (!show || !onDone) return;
    const t = setTimeout(onDone, durationMs);
    return () => clearTimeout(t);
  }, [show, durationMs, onDone]);

  if (!show) return null;
  return (
    <div className={`celebrate${contained ? " celebrate--contained" : ""}${night ? " celebrate--night" : ""}`} role="status" aria-live="polite" data-testid="celebration">
      {bits.map((b) => (
        <span key={b.key} className={`confetti confetti--${b.tone}`}
          style={{ left: `${b.left}%`, width: b.w, ["--delay" as string]: `${b.delay}s`, ["--dur" as string]: `${b.dur}s`,
            ["--dx" as string]: `${b.dx}px`, ["--spin" as string]: `${b.spin}deg`, ["--fall" as string]: contained ? "1150px" : "110vh" }} />
      ))}
      {message ? <div className="celebrate__msg">{message}{detail ? <small>{detail}</small> : null}</div> : null}
    </div>
  );
}
